import React, { type FC, useMemo } from 'react';

import { useSessionData } from '@confluence/session-data';

import type { CanvasCollaborator, CanvasCollaborators, Participant } from '../presenceTypes';
import { usePresenceStoreState } from '../presence-store';

import { ContentHeaderPresenceContainer } from './ContentHeaderPresenceContainer';

export type CanvasPresenceProps = {
	collaborators?: CanvasCollaborators | null;
	contentId: string;
	contentType: string;
	analyticSource?: string;
};

// don't have activity for canvas, just default to viewer
const ACTIVITY = 'viewer';

export const CanvasPresence: FC<CanvasPresenceProps> = ({
	collaborators,
	contentId,
	contentType,
	analyticSource,
}) => {
	const { participants: initialParticipants } = usePresenceStoreState();
	const { userId } = useSessionData();

	const createOnClickHandler = (collaborator: CanvasCollaborator) => {
		return collaborator.followCursor === true
			? () => {
					collaborator.onClick?.();
				}
			: collaborator.onClick;
	};

	const participants = useMemo(() => {
		const result: Participant[] = [];

		const currentParticipant = userId ? initialParticipants.get(userId) : undefined;

		// always want to use the currentParticipant instead of waiting for me from collaborators for performance
		if (currentParticipant) {
			result.push({
				...currentParticipant,
				activity: ACTIVITY,
			});
		}

		collaborators?.collaborators?.forEach((collaborator) => {
			// TODO MDS-1243 Until I get an aaid
			const id = collaborator.name;
			//always update, most recent session wins
			result.push({
				...collaborator,
				id,
				userId: id,
				src: collaborator.avatar.src ?? '',
				// we don't have
				activity: ACTIVITY,
				onClick: createOnClickHandler(collaborator),
			});
		});

		return result;
	}, [collaborators, initialParticipants, userId]);

	return (
		<ContentHeaderPresenceContainer
			contentId={contentId}
			// TODO MDS-1287 Canvas doesn’t have the concept of contentMode,
			// but we have spotlighting logic in ContentHeaderPresenceContainer that depends on contentMode=view.
			// Database will need change boarding, but whiteboard will not
			contentMode="view"
			contentType={contentType}
			analyticSource={analyticSource}
			onLoadMoreClick={() => {}}
			participants={participants}
			totalParticipants={participants.length}
		/>
	);
};
